// Edit details in the quote drawer (#1559): the event's date, time, guests,
// address and client, plus this quote's service type and event type. Lifted to
// the drawer so "Save and close" can save it with the quote draft.
import { useCallback, useEffect, useState, type Dispatch, type SetStateAction } from "react";
import { toast } from "sonner";
import type { QuoteRow } from "@/hooks/useQuotes";
import type { EventRow } from "@/hooks/useEvents";
import { useFindOrCreateContact, type Contact } from "@/hooks/useContacts";
import { useUpdateEventDetails, type EventDetailsPatch } from "@/hooks/useEventsPipeline";
import { validateEventDetails, valuesFrom, type EventDetailField, type EventDetailValues } from "@/lib/event-pipeline";
import { getErrorMessage } from "@/lib/error-message";
import { effectiveServiceType } from "@/types/quotes";
import { canEditQuote } from "./drawerLogic";
import type { SaveOverrides } from "./useQuoteDrawerDraft";
import { useChangeEventClient } from "./useQuoteActions";

export const EVENT_UPDATED = "Event updated. Every quote on it shows the change.";

type DetailsEvent = Pick<EventRow, "id" | "contact_id" | EventDetailField>;

interface Baseline {
  values: EventDetailValues;
  serviceType: string;
  eventTypeId: string;
  contactId: string | undefined;
  /** Clear was pressed: the only way Save details unlinks the contact. */
  clientCleared: boolean;
}

function baselineOf(event: DetailsEvent | null, quote: QuoteRow): Baseline {
  return {
    values: event ? valuesFrom(event) : valuesFrom({} as DetailsEvent),
    serviceType: effectiveServiceType(quote.service_type, quote.fulfillment_type),
    eventTypeId: quote.event_type_id ?? "",
    contactId: (event?.contact_id ?? quote.contact_id) || undefined,
    clientCleared: false,
  };
}

const sameBaseline = (a: Baseline, b: Baseline) => JSON.stringify(a) === JSON.stringify(b);

/** A contact for an event client typed by hand: found by email or created; never blocks the save. Undefined without an email or when the lookup fails. */
async function resolveNewContact(
  findOrCreate: ReturnType<typeof useFindOrCreateContact>,
  v: EventDetailValues,
): Promise<string | undefined> {
  const email = v.client_email.trim();
  if (!email) return undefined;
  const [first, ...rest] = v.client_name.trim().split(/\s+/);
  try {
    const contact = await findOrCreate.mutateAsync({
      first_name: first || v.client_name.trim(),
      last_name: rest.join(" ") || undefined,
      email,
      phone: v.client_phone.trim() || undefined,
    });
    return contact.id;
  } catch {
    // Non-blocking: the event still saves, and keeps the contact it had.
    return undefined;
  }
}

interface Options {
  /** Null while the event loads or after it failed to; Save details refuses then. */
  event: DetailsEvent | null;
  eventError?: boolean;
  quote: QuoteRow;
  /** The quote draft's save(), so the fresh status read applies (Approach §3). */
  saveQuote: (overrides: SaveOverrides) => Promise<boolean>;
  /** The quote draft is saving: Save details waits, since it may save the quote too. */
  quoteSaving: boolean;
}

/** The form state: values, the two quote selects and the client, against a baseline. */
function useDetailsState(event: DetailsEvent | null, quote: QuoteRow) {
  // Compared and stored as a string, so a refetch with the same values changes nothing.
  const latestKey = JSON.stringify(baselineOf(event, quote));
  // The props the baseline was taken from. After a save the baseline is what
  // was saved, and it follows the props again only once the refetch lands.
  const [seededKey, setSeededKey] = useState(latestKey);
  const [baseline, setBaseline] = useState<Baseline>(() => JSON.parse(latestKey));
  const [draft, setDraft] = useState<Baseline>(baseline);
  const [errors, setErrors] = useState<Partial<Record<EventDetailField, string>>>({});
  const dirty = !sameBaseline(draft, baseline);
  const reseed = useCallback(() => {
    const latest: Baseline = JSON.parse(latestKey);
    setSeededKey(latestKey);
    setBaseline(latest);
    setDraft(latest);
  }, [latestKey]);
  // A refetch (the event loading, a resync) shows at once while nothing is unsaved.
  useEffect(() => {
    if (latestKey !== seededKey && !dirty) reseed();
  }, [latestKey, seededKey, dirty, reseed]);

  const set = useCallback((key: EventDetailField, value: string) => {
    setDraft((d) => ({ ...d, values: { ...d.values, [key]: value } }));
  }, []);
  // Cancel: back to the baseline, or to the latest values if they moved meanwhile.
  const reset = () => {
    if (latestKey !== seededKey) reseed();
    else setDraft(baseline);
    setErrors({});
  };
  return { baseline, setBaseline, draft, setDraft, errors, setErrors, dirty, set, reset };
}

/** ContactSelector's three callbacks over the draft, as the old form wired them. */
function clientSetters(setDraft: Dispatch<SetStateAction<Baseline>>) {
  return {
    select: (c: Contact) =>
      setDraft((d) => ({
        ...d,
        contactId: c.id,
        clientCleared: false,
        values: { ...d.values, client_name: `${c.first_name} ${c.last_name || ""}`.trim(), client_email: c.email ?? "", client_phone: c.phone ?? "" },
      })),
    clear: () =>
      setDraft((d) => ({ ...d, contactId: undefined, clientCleared: true, values: { ...d.values, client_name: "", client_email: "", client_phone: "" } })),
    // Typing a client by hand drops the picked contact: Save details looks one
    // up by email, and keeps the current one when there's no email or no match.
    manualChange: (field: "name" | "email" | "phone", value: string) =>
      setDraft((d) => ({ ...d, contactId: undefined, values: { ...d.values, [`client_${field}`]: value } })),
  };
}

type ClearedClient = Partial<Record<"client_email" | "client_phone", null>>;

/**
 * validateEventDetails, except that picking another existing contact replaces
 * the client's name, email and phone as a set: a field the new contact
 * doesn't have is cleared, on the event and on its quotes, instead of
 * refused. A field typed blank by hand is still refused.
 */
function validateDetails(baseline: Baseline, draft: Baseline) {
  const { errors, patch } = validateEventDetails(baseline.values, draft.values);
  const cleared: ClearedClient = {};
  if (draft.contactId && draft.contactId !== baseline.contactId) {
    for (const key of ["client_email", "client_phone"] as const) {
      if (draft.values[key].trim() || !baseline.values[key].trim()) continue;
      delete errors[key];
      patch[key] = null;
      cleared[key] = null;
    }
  }
  return { errors, patch, cleared };
}

/**
 * Save details' writes, in order: the quote's two fields, the event, then the
 * client. Returns the contact the event ends up with, or false when it stopped.
 */
function useWriteDetails({ event, quote, saveQuote }: Options) {
  const updateEvent = useUpdateEventDetails();
  const findOrCreate = useFindOrCreateContact();
  const changeClient = useChangeEventClient();
  return async (baseline: Baseline, draft: Baseline, patch: EventDetailsPatch, cleared: ClearedClient): Promise<false | { contactId: string | undefined }> => {
    if (!event) {
      toast.error("The event hasn't loaded, so nothing was saved. Reload to try again.");
      return false;
    }
    // The quote first: a refused quote save stops the whole Save details.
    const typesChanged = draft.serviceType !== baseline.serviceType || draft.eventTypeId !== baseline.eventTypeId;
    if (typesChanged && canEditQuote(quote)) {
      if (!(await saveQuote({ serviceType: draft.serviceType, eventTypeId: draft.eventTypeId }))) return false;
    }
    try {
      await updateEvent.mutateAsync({ id: event.id, patch });
    } catch (err) {
      toast.error(getErrorMessage(err, "Couldn't update the event"));
      return false;
    }
    const kept = { contactId: baseline.contactId };
    if (draft.contactId === baseline.contactId && !draft.clientCleared) return kept;
    const found = draft.contactId ?? (await resolveNewContact(findOrCreate, draft.values));
    // Only Clear unlinks; a hand-typed client with no email or no match keeps the contact.
    const contactId = found ?? (draft.clientCleared ? undefined : baseline.contactId);
    if (contactId === baseline.contactId) return kept;
    return (await changeClient.run({ eventId: event.id, contactId: contactId ?? null, cleared })) && { contactId };
  };
}

export function useEventDetailsDraft(options: Options) {
  const s = useDetailsState(options.event, options.quote);
  const write = useWriteDetails(options);
  const [saving, setSaving] = useState(false);
  const { draft, baseline } = s;

  const save = async (): Promise<boolean> => {
    const { errors, patch, cleared } = validateDetails(baseline, draft);
    s.setErrors(errors);
    if (Object.keys(errors).length > 0) return false;
    setSaving(true);
    try {
      const written = await write(baseline, draft, patch, cleared);
      if (!written) return false;
      const saved = { ...draft, contactId: written.contactId, clientCleared: false };
      s.setBaseline(saved);
      s.setDraft(saved);
      toast.success(EVENT_UPDATED);
      return true;
    } finally {
      setSaving(false);
    }
  };

  return {
    // Edit details opens only once the event is here, so the form starts from its values.
    eventReady: !!options.event,
    eventError: !!options.eventError && !options.event,
    values: draft.values,
    set: s.set,
    serviceType: draft.serviceType,
    setServiceType: (v: string) => s.setDraft((d) => ({ ...d, serviceType: v })),
    eventTypeId: draft.eventTypeId,
    setEventTypeId: (v: string) => s.setDraft((d) => ({ ...d, eventTypeId: v })),
    contactId: draft.contactId,
    client: clientSetters(s.setDraft),
    typesChanged: draft.serviceType !== baseline.serviceType || draft.eventTypeId !== baseline.eventTypeId,
    errors: s.errors,
    dirty: s.dirty,
    save,
    saving: saving || options.quoteSaving,
    reset: s.reset,
  };
}

export type EventDetailsDraft = ReturnType<typeof useEventDetailsDraft>;
